import http from "@/helper/http";

const state = {
  // Fulfillment tickets
  tickets: [],
  ticketDetails: null,
  loadingTickets: false,
  loadingTicketDetails: false,

  // Loading states for actions
  creatingTicket: false,
  updatingTicketStatus: false,
  assigningTicket: false,

  // Pagination
  pagination: {
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
  },

  // Filters
  filters: {
    status: null,
    assigned_to_id: null,
  },

  // Error handling
  error: null,
};

const getters = {
  tickets: (state) => state.tickets,
  ticketDetails: (state) => state.ticketDetails,

  loadingTickets: (state) => state.loadingTickets,
  loadingTicketDetails: (state) => state.loadingTicketDetails,
  creatingTicket: (state) => state.creatingTicket,
  updatingTicketStatus: (state) => state.updatingTicketStatus,
  assigningTicket: (state) => state.assigningTicket,

  pagination: (state) => state.pagination,
  filters: (state) => state.filters,
  error: (state) => state.error,

  // Computed helpers
  pendingTickets: (state) =>
    state.tickets.filter((t) => t.status === "PENDING"),
  inProgressTickets: (state) =>
    state.tickets.filter((t) => t.status === "IN_PROGRESS"),
  completedTickets: (state) =>
    state.tickets.filter((t) => t.status === "COMPLETED"),

  getTicketById: (state) => (id) => {
    return state.tickets.find((t) => t.id === id);
  },
};

const mutations = {
  SET_TICKETS(state, tickets) {
    state.tickets = tickets;
  },

  SET_TICKET_DETAILS(state, details) {
    state.ticketDetails = details;
  },

  SET_LOADING_TICKETS(state, loading) {
    state.loadingTickets = loading;
  },

  SET_LOADING_TICKET_DETAILS(state, loading) {
    state.loadingTicketDetails = loading;
  },

  SET_CREATING_TICKET(state, creating) {
    state.creatingTicket = creating;
  },

  SET_UPDATING_TICKET_STATUS(state, updating) {
    state.updatingTicketStatus = updating;
  },

  SET_ASSIGNING_TICKET(state, assigning) {
    state.assigningTicket = assigning;
  },

  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination };
  },

  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },

  ADD_TICKET(state, ticket) {
    state.tickets.unshift(ticket);
  },

  UPDATE_TICKET(state, updatedTicket) {
    const index = state.tickets.findIndex((t) => t.id === updatedTicket.id);
    if (index !== -1) {
      state.tickets.splice(index, 1, updatedTicket);
    }
    if (state.ticketDetails?.id === updatedTicket.id) {
      state.ticketDetails = updatedTicket;
    }
  },

  UPDATE_TICKET_STATUS(state, { ticketId, status }) {
    const ticket = state.tickets.find((t) => t.id === ticketId);
    if (ticket) {
      ticket.status = status;
    }
    if (state.ticketDetails?.id === ticketId) {
      state.ticketDetails.status = status;
    }
  },

  UPDATE_TICKET_ASSIGNMENT(state, { ticketId, assignedTo }) {
    const ticket = state.tickets.find((t) => t.id === ticketId);
    if (ticket) {
      ticket.assigned_to_id = assignedTo?.id;
      ticket.assigned_to = assignedTo;
    }
    if (state.ticketDetails?.id === ticketId) {
      state.ticketDetails.assigned_to_id = assignedTo?.id;
      state.ticketDetails.assigned_to = assignedTo;
    }
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  CLEAR_ERROR(state) {
    state.error = null;
  },
};

const actions = {
  // ==================== FETCH TICKETS ====================

  async fetchTickets(
    { commit, state },
    { storeId, filters = {}, page, perPage } = {}
  ) {
    commit("SET_LOADING_TICKETS", true);
    commit("CLEAR_ERROR");

    try {
      const params = {
        page: page || state.pagination.page,
        per_page: perPage || state.pagination.per_page,
        ...state.filters,
        ...filters,
      };

      const response = await http.get(
        `/v2/store/${storeId}/fulfillment-tickets`,
        { params }
      );

      const data = response.data || {};
      commit("SET_TICKETS", data.items || []);
      commit("SET_PAGINATION", {
        page: data.page,
        per_page: data.per_page,
        total: data.total,
        pages: data.pages,
      });

      return data;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message || "Failed to load tickets";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_LOADING_TICKETS", false);
    }
  },

  async fetchTicketDetails({ commit }, { storeId, ticketId }) {
    commit("SET_LOADING_TICKET_DETAILS", true);
    commit("CLEAR_ERROR");

    try {
      const response = await http.get(
        `/v2/store/${storeId}/fulfillment-tickets/${ticketId}`
      );
      commit("SET_TICKET_DETAILS", response.data);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message ||
        "Failed to load ticket details";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_LOADING_TICKET_DETAILS", false);
    }
  },

  // ==================== CREATE/UPDATE ====================

  async createTicket({ commit }, { storeId, payload }) {
    commit("SET_CREATING_TICKET", true);
    commit("CLEAR_ERROR");

    try {
      const response = await http.post(
        `/v2/store/${storeId}/fulfillment-tickets`,
        payload
      );

      const ticket = response.data;
      commit("ADD_TICKET", ticket);
      return ticket;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message || "Failed to create ticket";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_CREATING_TICKET", false);
    }
  },

  async updateTicketStatus({ commit }, { storeId, ticketId, status, note }) {
    commit("SET_UPDATING_TICKET_STATUS", true);
    commit("CLEAR_ERROR");

    try {
      const response = await http.patch(
        `/v2/store/${storeId}/fulfillment-tickets/${ticketId}/status`,
        { status, note }
      );

      commit("UPDATE_TICKET_STATUS", { ticketId, status });
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message ||
        "Failed to update ticket status";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_UPDATING_TICKET_STATUS", false);
    }
  },

  async assignTicket({ commit }, { storeId, ticketId, assignedToId }) {
    commit("SET_ASSIGNING_TICKET", true);
    commit("CLEAR_ERROR");

    try {
      const response = await http.patch(
        `/v2/store/${storeId}/fulfillment-tickets/${ticketId}/assign`,
        { assigned_to_id: assignedToId }
      );

      const assignedTo = response.data?.assigned_to;
      commit("UPDATE_TICKET_ASSIGNMENT", { ticketId, assignedTo });
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.error?.message || "Failed to assign ticket";
      commit("SET_ERROR", message);
      throw error;
    } finally {
      commit("SET_ASSIGNING_TICKET", false);
    }
  },

  // ==================== FILTERS & PAGINATION ====================

  setFilters({ commit }, filters) {
    commit("SET_FILTERS", filters);
  },

  setPage({ commit }, page) {
    commit("SET_PAGINATION", { page });
  },

  clearCache({ commit }) {
    commit("SET_TICKETS", []);
    commit("SET_TICKET_DETAILS", null);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
